<template>
  <v-menu
    v-if="addressId"
    v-model="isOpen"
    :open-on-hover="!isMobile"
    :open-on-click="false"
    :open-on-focus="false"
    offset-y
    :close-on-content-click="false"
    :nudge-bottom="8"
    max-width="none"
    content-class="lpu-card-schedule-menu"
  >
    <template #activator="{ on, attrs }">
      <div
        v-bind="attrs"
        data-qa="analyses_lpu_card_schedule"
        class="text-body-2 primary--text cursor-pointer"
        v-on="isMobile ? {} : on"
        @click.stop="onActivatorClick"
      >
        {{ scheduleText }}
      </div>
    </template>

    <div class="lpu-card-schedule pa-4">
      <div
        v-if="isLoading && !schedule"
        class="lpu-card-schedule__skeleton"
      >
        <v-skeleton-loader type="text@14" />
      </div>

      <template v-else-if="schedule">
        <div class="d-flex flex-column gr-3">
          <div
            v-for="day in scheduleDays"
            :key="day"
            class="d-flex gc-4"
            :class="day === schedule.now_weekday_translit
              ? 'text-subtitle-1 text--text'
              : 'text-body-1 text-secondary--text'"
          >
            <span class="lpu-card-schedule__day flex-grow-1">
              {{ schedule.full_days[day] }}
            </span>
            <span class="flex-shrink-0">
              {{ schedule.schedule_days[day] }}
            </span>
          </div>
        </div>

        <template v-if="schedule.lpu_exceptions.length">
          <v-divider class="my-4" />

          <div class="d-flex flex-column gr-3">
            <div class="text-subtitle-1 text--text">
              График работы в праздники
            </div>
            <div
              v-for="(exception, index) in schedule.lpu_exceptions"
              :key="`holiday-${index}`"
              class="d-flex gc-4 text-body-1 text-secondary--text"
            >
              <span class="lpu-card-schedule__day flex-grow-1">
                {{ exception.date }}
              </span>
              <span class="flex-shrink-0">
                {{ exception.time }}
              </span>
            </div>
          </div>
        </template>
      </template>
    </div>
  </v-menu>

  <div
    v-else
    data-qa="analyses_lpu_card_schedule"
    class="text-body-2 primary--text"
  >
    {{ scheduleText }}
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useContextStore } from 'src/store'
import type { ContextStore } from 'src/components/pages/info/legal-case/types'
import apiLpuSchedule from 'src/components/pages/[town-translit]/diagnostika/analiz/components/LpuCard/api/apiLpuSchedule'
import type { LpuScheduleResponse } from 'src/components/pages/[town-translit]/diagnostika/analiz/components/LpuCard/api/apiLpuSchedule.types'

type Props = {
  scheduleText: string
  addressId?: number
}

const props = defineProps<Props>()

const { context }: ContextStore = useContextStore()
const isMobile = context.flavour === 'mobile'

const isOpen = ref(false)
const schedule = ref<LpuScheduleResponse | null>(null)
const isLoading = ref(false)
const isRequestFailed = ref(false)

const scheduleDays = computed(() => (
  schedule.value
    ? Object.keys(schedule.value.schedule_days)
    : []
))

const onActivatorClick = () => {
  if (!isMobile) {
    return
  }

  isOpen.value = !isOpen.value
}

async function loadSchedule() {
  if (!props.addressId || schedule.value || isLoading.value || isRequestFailed.value) {
    return
  }

  isLoading.value = true

  try {
    const { data } = await apiLpuSchedule.get(props.addressId)

    // eslint-disable-next-line require-atomic-updates
    schedule.value = data
  } catch {
    // eslint-disable-next-line require-atomic-updates
    isRequestFailed.value = true
  } finally {
    // eslint-disable-next-line require-atomic-updates
    isLoading.value = false
  }
}

watch(isOpen, isOpened => {
  if (isOpened) {
    loadSchedule()
  }
})
</script>

<style lang="scss" scoped>
@import 'src/styles/variables';

.lpu-card-schedule {
  min-width: 268px;

  &__day {
    flex-basis: 50%;
  }

  &__skeleton {
    min-width: 238px;
  }
}
</style>

<style lang="scss">
@import 'src/styles/variables';

.lpu-card-schedule-menu {
  box-shadow:
    0 2px 24px 0 rgba(9, 42, 72, 0.08),
    0 0 2px 0 rgba(21, 40, 92, 0.06),
    0 1px 2px 0 rgba(5, 23, 40, 0.08) !important;
  background-color: $ui-kit-bg-gray-0 !important;
  border-radius: $border-radius-lg !important;
  overflow: hidden;
}
</style>
